import { useEffect, useRef, useState } from "react";
import { guessNameFromSchoolEmail } from "../people/schoolEmailNameGuess";

export function StudentNameModal({
  open,
  email,
  savedGivenName,
  savedFamilyName,
  saving,
  onClose,
  onSave,
}: {
  readonly open: boolean;
  readonly email: string;
  readonly savedGivenName: string | null;
  readonly savedFamilyName: string | null;
  readonly saving: boolean;
  readonly onClose: () => void;
  readonly onSave: (givenName: string, familyName: string) => void | Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [givenName, setGivenName] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [guessed, setGuessed] = useState(false);
  const hasSaved = Boolean(savedGivenName?.trim() && savedFamilyName?.trim());

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const given = savedGivenName?.trim() ?? "";
    const family = savedFamilyName?.trim() ?? "";
    if (given && family) {
      setGivenName(given);
      setFamilyName(family);
      setGuessed(false);
      return;
    }
    const guess = guessNameFromSchoolEmail(email);
    setGivenName(guess?.givenName ?? "");
    setFamilyName(guess?.familyName ?? "");
    setGuessed(Boolean(guess));
  }, [open, email, savedGivenName, savedFamilyName]);

  return (
    <dialog
      ref={dialogRef}
      className="teacher-modal"
      aria-labelledby="student-name-title"
      onClose={onClose}
      onClick={(ev) => {
        if (ev.target === dialogRef.current && !saving) onClose();
      }}
    >
      <div className="teacher-modal-card" role="document">
        <h2 id="student-name-title" className="teacher-modal-title">
          Student name
        </h2>
        <p className="muted small" style={{ marginTop: 0 }}>
          {email || "This student"}
        </p>
        <form
          className="stack-form tight"
          onSubmit={(ev) => {
            ev.preventDefault();
            void onSave(givenName, familyName);
          }}
        >
          {guessed ? (
            <p className="muted small" style={{ margin: 0 }}>
              We guessed this from their school email. Please fix it if it is wrong.
            </p>
          ) : null}
          <label className="field">
            <span className="field-label">First name</span>
            <input
              className="input"
              value={givenName}
              onChange={(ev) => setGivenName(ev.target.value)}
              disabled={saving}
              autoComplete="off"
            />
          </label>
          <label className="field">
            <span className="field-label">Last name</span>
            <input
              className="input"
              value={familyName}
              onChange={(ev) => setFamilyName(ev.target.value)}
              disabled={saving}
              autoComplete="off"
            />
          </label>
          <div className="teacher-modal-actions">
            <button type="button" className="btn secondary" disabled={saving} onClick={onClose}>
              Cancel
            </button>
            {hasSaved ? (
              <button
                type="button"
                className="btn secondary"
                disabled={saving}
                onClick={() => void onSave("", "")}
              >
                Clear name
              </button>
            ) : null}
            <button
              type="submit"
              className="btn primary"
              disabled={saving || !givenName.trim() || !familyName.trim()}
            >
              {saving ? "Saving…" : "Save name"}
            </button>
          </div>
        </form>
      </div>
    </dialog>
  );
}
